import React, { useState, useEffect } from 'react';
import { Send, User, MessageSquare, Clock } from 'lucide-react';

interface Message {
    ID: number;
    Sender: 'student' | 'admin';
    Content: string;
    Timestamp: string;
}

interface AdminMessagingProps {
    studentId: string;
    context: string;
}

const AdminMessaging: React.FC<AdminMessagingProps> = ({ studentId, context }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/admin/student/${studentId}/messages?context=${context}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, [studentId, context]);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/admin/student/${studentId}/messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Context: context,
                    Content: newMessage
                })
            });

            if (res.ok) {
                setNewMessage('');
                fetchMessages();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-[380px] bg-[#F8FAFC] rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                    <div className="flex items-center justify-center h-full text-xs text-slate-300">No messages yet</div>
                )}
                {messages.map((msg) => (
                    <div key={msg.ID} className={`flex flex-col ${msg.Sender === 'admin' ? 'items-end' : 'items-start'}`}>
                        <div className={`
                            max-w-[85%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed
                            ${msg.Sender === 'admin'
                                ? 'bg-[#009B8F] text-white rounded-tr-sm'
                                : 'bg-white text-[#011E26] border border-slate-200 rounded-tl-sm shadow-sm'}
                        `}>
                            {msg.Content}
                        </div>
                        <span className="text-[9px] text-slate-400 mt-1 px-1">
                            {new Date(msg.Timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ))}
            </div>

            <form onSubmit={handleSend} className="p-3 bg-white border-t border-slate-100 flex gap-2">
                <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type response..."
                    className="flex-1 bg-slate-50 border border-slate-200 outline-none px-3.5 py-2 rounded-lg text-sm focus:ring-2 focus:ring-[#009B8F]/20 focus:border-[#009B8F]/30 transition-all"
                />
                <button
                    disabled={loading}
                    type="submit"
                    className="p-2 bg-[#009B8F] text-white rounded-lg hover:bg-[#00857a] disabled:opacity-50 transition-colors"
                >
                    <Send size={15} />
                </button>
            </form>
        </div>
    );
};

export default AdminMessaging;
